// StageForms.jsx
import React, { useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const StageForms = ({
  status,
  inputs = {}, // Default to empty object
  onInputChange,
  files = {},
  savedFiles = {},
  onFileUpload,
  onCancel,
  onConfirm,
  isReadOnly = false 
}) => {

  const fileRefs = useRef({});

  const triggerFileClick = (key) => {
    if (fileRefs.current && fileRefs.current[key]) fileRefs.current[key].click();
  };

  const formStyle = { background: "#eff6ff", padding: "16px", borderRadius: "8px", border: "1px solid #dbeafe" };
  const labelStyle = { fontSize: "0.85rem", fontWeight: "600", color: "#495057", marginBottom: "4px" };
  
  // Dynamic style based on ReadOnly state
  const baseInputStyle = { border: "1px solid #ced4da", color: "#212529", fontSize: "0.9rem", padding: "8px" };
  const inputStyle = isReadOnly 
    ? { ...baseInputStyle, backgroundColor: "#e9ecef", cursor: "default" } // Greyed out
    : { ...baseInputStyle, backgroundColor: "#fff" };                      // White

  // Robust date formatting: handle strings (`YYYY-MM-DD` or ISO), numbers (unix ms), Date objects
  const formatDate = (val) => {
    if (val === null || val === undefined || val === "") return "";
    try {
      // If already a string that looks like YYYY-MM-DD, return it
      if (typeof val === "string") {
        // if ISO timestamp
        if (val.includes("T")) return val.split("T")[0];
        // if plain yyyy-mm-dd or other, try to parse to Date and reformat
        const d = new Date(val);
        if (!Number.isNaN(d.getTime())) {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        }
        return val;
      }

      if (typeof val === "number") {
        // assume epoch ms (safe fallback)
        const d = new Date(val);
        if (!Number.isNaN(d.getTime())) {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        }
        return "";
      }

      if (val instanceof Date) {
        if (!Number.isNaN(val.getTime())) {
          const yyyy = val.getFullYear();
          const mm = String(val.getMonth() + 1).padStart(2, "0");
          const dd = String(val.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        }
        return "";
      }

      // Fallback to string
      return String(val).split("T")[0] || "";
    } catch (e) {
      return "";
    }
  };

  // Helper to safely get value while preserving falsy legitimate values like 0
  const val = (key) => {
    if (!inputs) return "";
    return Object.prototype.hasOwnProperty.call(inputs, key) && inputs[key] !== null && inputs[key] !== undefined
      ? inputs[key]
      : "";
  };

  // Allow onInputChange to be passed either as an event handler or as (name, value)
  const emitInputChange = (evOrName, maybeValue) => {
    if (isReadOnly) return; // Block changes if readOnly

    if (!onInputChange) return;
    // If first arg is an event (has target), pass it straight through
    if (evOrName && evOrName.target) {
      onInputChange(evOrName);
      return;
    }
    // Otherwise treat as (name, value)
    const name = evOrName;
    const value = maybeValue;
    // construct a synthetic event to keep parent's handler consistent
    const synthetic = {
      target: { name, value }
    };
    onInputChange(synthetic);
  };

  const renderFileInput = (label, key, accept = "*") => {
    const hasNewFile = files && files[key];
    let hasSavedFile = null;

    // savedFiles might be string url, or array, or object map — be flexible
    if (!savedFiles) hasSavedFile = null;
    else if (typeof savedFiles === "string") hasSavedFile = savedFiles;
    else if (Array.isArray(savedFiles) && savedFiles.length > 0) {
      // Try find by name or take first
      const found = savedFiles.find(f => (f.fieldName === key || f.name?.startsWith(`${key}-`) || f.url?.includes(`/${key}-`)));
      // Prefer the full object if found, otherwise fallback
      hasSavedFile = found ? found : (savedFiles[0] && (savedFiles[0].url || savedFiles[0]));
    } else if (typeof savedFiles === "object") {
      // object map
      hasSavedFile = savedFiles[key] || null;
    } else {
      hasSavedFile = null;
    }

    // ---------------------------------------------------------
    // [MODIFIED] Special Display for LiDAR (.las) files
    // ---------------------------------------------------------
    if (key === "lasFile" && !hasNewFile && hasSavedFile) {
        return (
            <div className="mb-3">
                <label style={labelStyle}>{label}</label>
                <div className={`d-flex align-items-center gap-2 border rounded p-2 ${isReadOnly ? 'bg-light' : 'bg-white'}`}>
                    
                    {!isReadOnly && (
                        <button type="button" className="btn btn-sm btn-light border" onClick={() => triggerFileClick(key)}>
                           Change
                        </button>
                    )}

                    <div className="d-flex align-items-center text-success small fw-bold px-2" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)', borderRadius: '4px' }}>
                        <i className="bi bi-box-seam me-2 fs-5"></i>
                        <span>3D Scan Processed & Available</span>
                    </div>

                    {!isReadOnly && (
                        <input
                            type="file"
                            hidden
                            accept={accept}
                            ref={el => { if (el) fileRefs.current[key] = el; }}
                            onChange={(e) => {
                                const f = e.target.files && e.target.files[0];
                                if (f) onFileUpload && onFileUpload(key, f);
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }
    // ---------------------------------------------------------

    return (
      <div className="mb-3">
        <label style={labelStyle}>{label}</label>
        <div className={`d-flex align-items-center gap-2 border rounded p-2 ${isReadOnly ? 'bg-light' : 'bg-white'}`}>
          
          {/* Hide Upload Button if Read Only */}
          {!isReadOnly && (
            <button type="button" className="btn btn-sm btn-light border" onClick={() => triggerFileClick(key)}>
              {hasNewFile ? "Change" : "Choose"}
            </button>
          )}

          <div className="d-flex flex-column justify-content-center overflow-hidden" style={{ maxWidth: "200px" }}>
            {hasNewFile && (
              <span className="small text-success fw-bold text-truncate"><i className="bi bi-cloud-upload-fill me-1"></i>{files[key].name}</span>
            )}
            {!hasNewFile && hasSavedFile && (
              <a 
                href={typeof hasSavedFile === 'string' ? hasSavedFile : hasSavedFile.url} 
                target="_blank" 
                rel="noreferrer" 
                className="small text-primary text-decoration-underline text-truncate"
              >
                <i className="bi bi-paperclip me-1"></i>View Existing
              </a>
            )}
            {!hasNewFile && !hasSavedFile && <span className="small text-muted fst-italic">No file attached</span>}
          </div>

          {/* Hide Input if Read Only */}
          {!isReadOnly && (
            <input
              type="file"
              hidden
              accept={accept}
              ref={el => { if (el) fileRefs.current[key] = el; }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (f) {
                  // notify parent
                  onFileUpload && onFileUpload(key, f);
                }
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const renderButtons = () => {
    // Don't render buttons if Read Only
    if (isReadOnly) return null;

    return (
      <div className="d-flex gap-2 mt-4">
        <button type="button" className="btn btn-sm btn-light border bg-white flex-fill" onClick={() => onCancel && onCancel()}>Cancel</button>
        <button type="button" className="btn btn-sm btn-primary fw-bold flex-fill" onClick={() => onConfirm && onConfirm()}>Save / Update</button>
      </div>
    );
  };

  switch (status) {
    case "Not Started":
      return (
        <div style={formStyle}>
          <div className="d-flex align-items-center mb-2"><i className="bi bi-circle-fill text-secondary me-2" style={{ fontSize: "8px" }}></i><h6 className="fw-bold m-0 text-dark">Pre-Acquisition</h6></div>
          <div className="row g-2 mb-2">
            <div className="col-12">
              <label style={labelStyle}>Surveyor Name</label>
              <input
                type="text"
                className="form-control"
                style={inputStyle}
                name="surveyor_name"
                value={val('surveyor_name')}
                onChange={emitInputChange}
                readOnly={isReadOnly}
              />
            </div>
            <div className="col-6">
              <label style={labelStyle}>Scheduled Date</label>
              <input
                type="date"
                className="form-control"
                style={inputStyle}
                name="survey_date"
                value={formatDate(val('survey_date'))}
                onChange={emitInputChange}
                readOnly={isReadOnly}
              />
            </div>
            <div className="col-6">
              <label style={labelStyle}>Est. Land Area</label>
              <input
                type="number"
                className="form-control"
                style={inputStyle}
                name="est_land_area"
                value={val('est_land_area')}
                onChange={emitInputChange}
                readOnly={isReadOnly}
              />
            </div>
          </div>
          {renderFileInput("Attach Assignment Letter", "surveyLetter", ".pdf")}
          {renderButtons()}
        </div>
      );

    case "Survey":
      return (
        <div style={formStyle}>
          <div className="d-flex align-items-center mb-2"><i className="bi bi-circle-fill text-primary me-2" style={{ fontSize: "8px" }}></i><h6 className="fw-bold m-0 text-dark">Survey Details</h6></div>
          <div className="row g-2 mb-2">
            <div className="col-12">
              <label style={labelStyle}>Confirmed Owner</label>
              <input type="text" className="form-control" style={inputStyle} name="confirmed_owner" value={val('confirmed_owner')} onChange={emitInputChange} readOnly={isReadOnly} />
            </div>
            <div className="col-6">
              <label style={labelStyle}>Final Land (m²)</label>
              <input type="number" className="form-control" style={inputStyle} name="final_land_area" value={val('final_land_area')} onChange={emitInputChange} readOnly={isReadOnly} />
            </div>
            <div className="col-6">
              <label style={labelStyle}>Final Bld (m²)</label>
              <input type="number" className="form-control" style={inputStyle} name="final_building_area" value={val('final_building_area')} onChange={emitInputChange} readOnly={isReadOnly} />
            </div>
          </div>
          {renderFileInput("Upload Survey Report (PDF)", "surveyReport", ".pdf")}
          {renderFileInput("Upload 3D Scan (.LAS)", "lasFile", ".las")}
          {renderButtons()}
        </div>
      );

    case "Valuation":
      return (
        <div style={formStyle}>
          <div className="d-flex align-items-center mb-2"><i className="bi bi-circle-fill text-info me-2" style={{ fontSize: "8px" }}></i><h6 className="fw-bold m-0 text-dark">Valuation Details</h6></div>
          <div className="mb-2">
            <label style={labelStyle}>Appraisal Firm</label>
            <input type="text" className="form-control" style={inputStyle} name="appraiser_firm" value={val('appraiser_firm')} onChange={emitInputChange} readOnly={isReadOnly} />
          </div>
          <div className="mb-3">
            <label style={labelStyle}>Appraised Value (SAR)</label>
            <div className="input-group input-group-sm">
              <span className="input-group-text" style={isReadOnly ? { backgroundColor: "#e9ecef" } : {}}>$</span>
              <input type="number" className="form-control" style={inputStyle} name="appraised_value" value={val('appraised_value')} onChange={emitInputChange} readOnly={isReadOnly} />
            </div>
          </div>
          {renderFileInput("Official Valuation Report", "valReport", ".pdf,.xlsx")}
          {renderButtons()}
        </div>
      );

    case "Negotiation":
      return (
        <div style={formStyle}>
          <div className="d-flex align-items-center mb-2"><i className="bi bi-circle-fill text-warning me-2" style={{ fontSize: "8px" }}></i><h6 className="fw-bold m-0 text-dark">Negotiation Details</h6></div>
          <div className="mb-2">
            <label style={labelStyle}>Final Agreed Price</label>
            <div className="input-group input-group-sm">
              <span className="input-group-text" style={isReadOnly ? { backgroundColor: "#e9ecef" } : {}}>$</span>
              <input type="number" className="form-control" style={inputStyle} name="final_agreed_price" value={val('final_agreed_price')} onChange={emitInputChange} readOnly={isReadOnly} />
            </div>
          </div>
          <div className="mb-3">
            <label style={labelStyle}>Owner Decision</label>
            <select className="form-select form-select-sm" name="owner_decision" value={val('owner_decision')} onChange={emitInputChange} disabled={isReadOnly}>
              <option value="">Select...</option>
              <option value="Accepted">Accepted Offer</option>
              <option value="Consignment">Court Consignment</option>
            </select>
          </div>
          {renderFileInput("Signed Minutes of Meeting", "momSigned", ".pdf")}
          {renderButtons()}
        </div>
      );

    case "Payment":
      return (
        <div style={formStyle}>
          <div className="d-flex align-items-center mb-2"><i className="bi bi-circle-fill text-danger me-2" style={{ fontSize: "8px" }}></i><h6 className="fw-bold m-0 text-dark">Payment Details</h6></div>
          <div className="mb-2">
            <label style={labelStyle}>Payment Reference (Check/Transfer)</label>
            <input type="text" className="form-control" style={inputStyle} name="payment_reference" value={val('payment_reference')} onChange={emitInputChange} readOnly={isReadOnly} />
          </div>
          <div className="mb-3">
            <label style={labelStyle}>Payment Date</label>
            <input type="date" className="form-control" style={inputStyle} name="payment_date" value={formatDate(val('payment_date'))} onChange={emitInputChange} readOnly={isReadOnly} />
          </div>
          {renderFileInput("Proof of Payment / Receipt", "paymentProof", ".jpg,.png,.pdf")}
          {renderButtons()}
        </div>
      );

    case "Acquired":
      return (
        <div style={formStyle}>
          <div className="d-flex align-items-center mb-2"><i className="bi bi-circle-fill text-success me-2" style={{ fontSize: "8px" }}></i><h6 className="fw-bold m-0 text-dark">Acquisition & Demolition</h6></div>
          <div className="alert alert-warning py-2 px-3 small mb-3">Ensure property is vacant before demolition.</div>
          <div className="mb-2">
            <label style={labelStyle}>Demolition Contractor</label>
            <input type="text" className="form-control" style={inputStyle} name="demolition_contractor" value={val('demolition_contractor')} onChange={emitInputChange} readOnly={isReadOnly} />
          </div>
          <div className="mb-3">
            <label style={labelStyle}>Planned Demolition Date</label>
            <input type="date" className="form-control" style={inputStyle} name="planned_demo_date" value={formatDate(val('planned_demo_date'))} onChange={emitInputChange} readOnly={isReadOnly} />
          </div>
          {renderFileInput("Handover / Vacancy Cert", "vacancyCert", ".pdf")}
          {renderButtons()}
        </div>
      );

    case "Demolished":
      return (
        <div className="text-center p-3 text-muted">
          <i className="bi bi-check-circle-fill text-success fs-1 mb-2"></i>
          <h6 className="fw-bold">Workflow Complete</h6>
          <p className="small mb-0">This property has been demolished and the land is now cleared.</p>
        </div>
      );

    default:
      return null;
  }
};

export default StageForms;